import React, { createContext, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import { createChatSocket } from 'src/app/utils/socket';
import { useAppDispatch, useAppSelector } from 'src/services/redux/app/store';
import { _setMessagesUnread } from 'src/services/redux/slices/unread/unreads-slice';
import { _logOut } from 'src/services/redux/slices/auth/auth-slice';
import { toast } from 'react-toastify';
import { chatApi } from '../services/chatApi';
import { useConversations } from '../services/chatHooks';
import { ChatAttachment, ChatConversation, ChatMessage, ChatNotification } from '../types';
import { toMs } from '../utils/lastSeen';

export interface ChatSocketContextValue {
  connected: boolean;
  myId: number;
  onlineUsers: number[];
  isOnline: (userId: number) => boolean;
  // Kullanıcının son görülme zamanı (epoch-ms). Canlı presence olayı yoksa
  // fallback (backend last_activity_at) değerlendirilir. null → bilinmiyor.
  lastSeenOf: (userId: number, fallback?: string | number | null) => number | null;
  typingByConv: Record<number, number[]>;
  readByConv: Record<number, Record<number, number>>;
  messagesByConv: Record<number, ChatMessage[]>;
  hasMoreByConv: Record<number, boolean>;
  loadingByConv: Record<number, boolean>;
  activeConvId: number | null;
  notifications: ChatNotification[];
  dismissNotification: (key: number) => void;

  openConversation: (conversationId: number) => void;
  closeConversation: (conversationId: number) => void;
  clearActive: () => void;
  loadOlder: (conversationId: number) => void;
  sendMessage: (conversationId: number, payload: { content?: string; attachments?: ChatAttachment[]; replyToId?: number | null }) => void;
  editMessage: (messageId: number, content: string) => void;
  deleteMessage: (messageId: number, conversationId: number) => void;
  addReaction: (messageId: number, emoji: string) => void;
  removeReaction: (messageId: number, emoji: string) => void;
  startTyping: (conversationId: number) => void;
  stopTyping: (conversationId: number) => void;
  markRead: (conversationId: number, lastMessageId: number) => void;
  joinChannel: (conversationId: number) => void;
  leaveChannel: (conversationId: number) => void;

  // Canlı aktivite takibi (admin/live-activity)
  socket: Socket | null;
  trackRoute: (route: string, title?: string) => void;
  trackAction: (action: { type?: string; label: string }) => void;
}

export const ChatSocketContext = createContext<ChatSocketContextValue | null>(null);

const CONV_KEY = ['chat', 'conversations'];

export const ChatSocketProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const token = useAppSelector((s) => s.auth.tokens?.access?.token);
  const user = useAppSelector((s) => s.auth.user);
  const myId = Number(user?.id) || 0;
  const { data: conversationsData } = useConversations();

  const socketRef = useRef<Socket | null>(null);
  const forcedOutRef = useRef(false); // session:revoked sonrası tekrar tetiklenmesin
  const [connected, setConnected] = useState(false);
  const [socketInstance, setSocketInstance] = useState<Socket | null>(null);
  const [onlineUsers, setOnlineUsers] = useState<number[]>([]);
  const onlineUsersRef = useRef<number[]>([]);
  onlineUsersRef.current = onlineUsers;
  const [lastSeen, setLastSeen] = useState<Record<number, number>>({});
  const [notifications, setNotifications] = useState<ChatNotification[]>([]);
  const notifCounterRef = useRef(0);
  const dismissNotification = useCallback((key: number) => setNotifications((prev) => prev.filter((n) => n.key !== key)), []);
  const [typingByConv, setTypingByConv] = useState<Record<number, number[]>>({});
  const [readByConv, setReadByConv] = useState<Record<number, Record<number, number>>>({});
  const [hasMoreByConv, setHasMoreByConv] = useState<Record<number, boolean>>({});
  const [loadingByConv, setLoadingByConv] = useState<Record<number, boolean>>({});
  const [activeConvId, setActiveConvIdState] = useState<number | null>(null);

  const [messagesByConv, setMessagesState] = useState<Record<number, ChatMessage[]>>({});
  const messagesRef = useRef<Record<number, ChatMessage[]>>({});
  const setMessages = useCallback((updater: (prev: Record<number, ChatMessage[]>) => Record<number, ChatMessage[]>) => {
    setMessagesState((prev) => {
      const next = updater(prev);
      messagesRef.current = next;
      return next;
    });
  }, []);

  const activeConvIdRef = useRef<number | null>(null);
  const setActiveConvId = (id: number | null) => {
    activeConvIdRef.current = id;
    setActiveConvIdState(id);
  };

  const tempCounterRef = useRef(0);
  const typingEmitRef = useRef<Record<number, number>>({});
  const typingStopTimerRef = useRef<Record<number, any>>({});
  const typingExpireRef = useRef<Record<string, any>>({});

  // ── Cache yardımcıları ──────────────────────────────────────────────────────
  const patchConversations = useCallback(
    (convId: number, patchFn: (c: ChatConversation) => Partial<ChatConversation>, moveTop = false) => {
      let missing = false;
      queryClient.setQueryData<ChatConversation[]>(CONV_KEY, (old) => {
        if (!old) {
          missing = true;
          return old;
        }
        const idx = old.findIndex((c) => c.id === convId);
        if (idx < 0) {
          missing = true;
          return old;
        }
        const patched = { ...old[idx], ...patchFn(old[idx]) };
        if (moveTop) return [patched, ...old.filter((c) => c.id !== convId)];
        const copy = [...old];
        copy[idx] = patched;
        return copy;
      });
      if (missing) queryClient.invalidateQueries(CONV_KEY);
    },
    [queryClient],
  );

  const getConvUnread = useCallback(
    (convId: number): number => {
      const list = queryClient.getQueryData<ChatConversation[]>(CONV_KEY);
      return list?.find((c) => c.id === convId)?.unreadCount || 0;
    },
    [queryClient],
  );

  // ── Gelen mesajı belleğe yerleştir (tempId ile optimistic uzlaştırma + dedupe) ─
  const reconcileIncoming = useCallback(
    (msg: ChatMessage) => {
      setMessages((prev) => {
        const existing = prev[msg.conversationId];
        if (!existing) return prev; // conv bellekte yüklü değil -> sidebar ayrı güncellenir
        const list = [...existing];
        if (msg.tempId) {
          const i = list.findIndex((m) => m.tempId === msg.tempId);
          if (i >= 0) {
            list[i] = { ...msg, pending: false };
            return { ...prev, [msg.conversationId]: list };
          }
        }
        if (list.some((m) => m.id === msg.id)) return prev;
        list.push({ ...msg, pending: false });
        return { ...prev, [msg.conversationId]: list };
      });
    },
    [setMessages],
  );

  const applyUpdate = useCallback(
    (u: { conversationId: number; id: number; content: string; editedAt: string }) => {
      setMessages((prev) => {
        const list = prev[u.conversationId];
        if (!list) return prev;
        return { ...prev, [u.conversationId]: list.map((m) => (m.id === u.id ? { ...m, content: u.content, editedAt: u.editedAt } : m)) };
      });
    },
    [setMessages],
  );

  const applyDelete = useCallback(
    (d: { conversationId: number; id: number }) => {
      setMessages((prev) => {
        const list = prev[d.conversationId];
        if (!list) return prev;
        return {
          ...prev,
          [d.conversationId]: list.map((m) => (m.id === d.id ? { ...m, content: null, attachments: [], deletedAt: new Date().toISOString() } : m)),
        };
      });
    },
    [setMessages],
  );

  const applyReaction = useCallback(
    (r: { conversationId: number; messageId: number; reactions: any[] }) => {
      setMessages((prev) => {
        const list = prev[r.conversationId];
        if (!list) return prev;
        return { ...prev, [r.conversationId]: list.map((m) => (m.id === r.messageId ? { ...m, reactions: r.reactions } : m)) };
      });
    },
    [setMessages],
  );

  const applyTyping = useCallback((t: { conversationId: number; userId: number; typing: boolean }) => {
    const key = `${t.conversationId}:${t.userId}`;
    setTypingByConv((prev) => {
      const cur = new Set(prev[t.conversationId] || []);
      if (t.typing) cur.add(t.userId);
      else cur.delete(t.userId);
      return { ...prev, [t.conversationId]: Array.from(cur) };
    });
    clearTimeout(typingExpireRef.current[key]);
    if (t.typing) {
      typingExpireRef.current[key] = setTimeout(() => {
        setTypingByConv((prev) => ({ ...prev, [t.conversationId]: (prev[t.conversationId] || []).filter((u) => u !== t.userId) }));
      }, 4000);
    }
  }, []);

  const applyRead = useCallback(
    (r: { conversationId: number; userId: number; lastReadMessageId: number }) => {
      setReadByConv((prev) => ({
        ...prev,
        [r.conversationId]: { ...(prev[r.conversationId] || {}), [r.userId]: r.lastReadMessageId },
      }));
      // Kendi diğer sekmemden okundu -> unread'i sıfırla
      if (Number(r.userId) === myId) {
        const prevUnread = getConvUnread(r.conversationId);
        if (prevUnread > 0) {
          patchConversations(r.conversationId, () => ({ unreadCount: 0 }));
        }
      }
    },
    [getConvUnread, myId, patchConversations],
  );

  // ── Okundu işaretle (emit/REST + rozet senkronu) ────────────────────────────
  const markRead = useCallback(
    (conversationId: number, lastMessageId: number) => {
      if (!lastMessageId || lastMessageId < 0) return;
      const s = socketRef.current;
      if (s && s.connected) s.emit('read:mark', { conversationId, lastReadMessageId: lastMessageId });
      else chatApi.markRead(conversationId, lastMessageId).catch(() => undefined);
      setReadByConv((prev) => ({ ...prev, [conversationId]: { ...(prev[conversationId] || {}), [myId]: lastMessageId } }));
      const prevUnread = getConvUnread(conversationId);
      if (prevUnread > 0) {
        patchConversations(conversationId, () => ({ unreadCount: 0, lastReadMessageId: lastMessageId }));
      }
    },
    [getConvUnread, myId, patchConversations],
  );

  const handleActivity = useCallback(
    (payload: { conversationId: number; message: ChatMessage; senderId: number }) => {
      const { conversationId, message, senderId } = payload;
      const isActive = conversationId === activeConvIdRef.current;
      const mine = Number(senderId) === myId;
      patchConversations(
        conversationId,
        (c) => ({
          lastMessage: {
            content: message.content,
            createdAt: message.createdAt,
            senderId: message.senderId,
            hasAttachment: (message.attachments || []).length > 0,
          },
          lastMessageAt: message.createdAt,
          unreadCount: isActive || mine ? c.unreadCount : (c.unreadCount || 0) + 1,
        }),
        true,
      );
      // Gönderen çevrimiçiyse ve sohbet açık değilse animasyonlu bildirim
      if (!isActive && !mine && onlineUsersRef.current.includes(Number(senderId))) {
        const conv = queryClient.getQueryData<ChatConversation[]>(CONV_KEY)?.find((c) => c.id === conversationId);
        const key = (notifCounterRef.current += 1);
        // Masaüstü uygulamasında OS bildirim merkezine de düşsün (web'de tanımsızdır)
        const desktop = (window as any).aycrmDesktop;
        if (desktop) {
          const title =
            conv?.type === 'channel'
              ? `#${conv?.name || 'kanal'}`
              : `${message.senderName || ''} ${message.senderSurname || ''}`.trim() || 'Yeni mesaj';
          desktop.notify({
            title,
            body: message.content || 'Yeni mesaj',
            route: `/messages?conversation=${conversationId}`,
          });
        }
        setNotifications((prev) =>
          [
            {
              key,
              conversationId,
              senderId: Number(senderId),
              senderName: message.senderName,
              senderSurname: message.senderSurname,
              content: message.content,
              isChannel: conv?.type === 'channel',
              channelName: conv?.name,
            },
            ...prev,
          ].slice(0, 4),
        );
      }
    },
    [myId, patchConversations, queryClient],
  );

  // Son olay handler'larını ref'te tut (socket effect yalnız token'a bağlı)
  const handlersRef = useRef<any>({});
  handlersRef.current = {
    onMessageNew: (msg: ChatMessage) => {
      reconcileIncoming(msg);
      if (msg.conversationId === activeConvIdRef.current && Number(msg.senderId) !== myId) {
        markRead(msg.conversationId, msg.id);
      }
    },
    handleActivity,
    applyUpdate,
    applyDelete,
    applyReaction,
    applyTyping,
    applyRead,
    onPresenceSnapshot: (p: { online: number[] }) => setOnlineUsers(p.online || []),
    onPresenceUpdate: (p: { userId: number; online: boolean; lastSeen?: number }) => {
      setOnlineUsers((prev) => (p.online ? Array.from(new Set([...prev, p.userId])) : prev.filter((u) => u !== p.userId)));
      // Çevrimdışı olan kullanıcının son görülme zamanını kaydet (yeni değeri koru).
      if (!p.online && p.lastSeen) {
        setLastSeen((prev) => ({ ...prev, [p.userId]: Math.max(prev[p.userId] || 0, Number(p.lastSeen)) }));
      }
    },
    onConversationCreated: () => queryClient.invalidateQueries(CONV_KEY),
    onMemberChange: (m: { conversationId: number }) => queryClient.invalidateQueries(['chat', 'members', m.conversationId]),
  };

  // ── Socket yaşam döngüsü (token değişince yeniden kur) ───────────────────────
  useEffect(() => {
    if (!token) return undefined;
    const socket = createChatSocket(token);
    socketRef.current = socket;
    setSocketInstance(socket);

    socket.on('connect', () => {
      setConnected(true);
      const active = activeConvIdRef.current;
      if (active) socket.emit('conversation:open', { conversationId: active });
      queryClient.invalidateQueries(CONV_KEY);
    });
    socket.on('disconnect', () => setConnected(false));
    socket.on('presence:snapshot', (p: any) => handlersRef.current.onPresenceSnapshot(p));
    socket.on('presence:update', (p: any) => handlersRef.current.onPresenceUpdate(p));
    socket.on('message:new', (m: any) => handlersRef.current.onMessageNew(m));
    socket.on('conversation:activity', (p: any) => handlersRef.current.handleActivity(p));
    socket.on('message:updated', (u: any) => handlersRef.current.applyUpdate(u));
    socket.on('message:deleted', (d: any) => handlersRef.current.applyDelete(d));
    socket.on('reaction:update', (r: any) => handlersRef.current.applyReaction(r));
    socket.on('typing:update', (t: any) => handlersRef.current.applyTyping(t));
    socket.on('read:update', (r: any) => handlersRef.current.applyRead(r));
    socket.on('conversation:created', () => handlersRef.current.onConversationCreated());
    socket.on('member:joined', (m: any) => handlersRef.current.onMemberChange(m));
    socket.on('member:left', (m: any) => handlersRef.current.onMemberChange(m));
    // iCloud (Apple Notes) senkronu — backend değişimde push eder, polling yerine
    socket.on('icloud:notesUpdated', () => {
      queryClient.invalidateQueries(['icloud-notes']);
      queryClient.invalidateQueries(['icloud-status']);
    });
    // Menü Yönetimi — yönetici menüyü değiştirince açık oturumlar yeniden çeker
    socket.on('menu:updated', () => {
      queryClient.invalidateQueries(['menu-items']);
    });
    // Partner Hub — portal webhook'u (davet kabulü, giriş, onay bekleyen kişi) listeyi tazeler
    socket.on('partner-hub:changed', (p: any) => {
      queryClient.invalidateQueries(['partner-hub']);
      if (p && p.account_id) queryClient.invalidateQueries(['apple-program', 'account', p.account_id]);
    });

    // Oturum düşürme — hesap pasife alındı / yönetici oturumları sonlandırdı.
    // Backend önce bu olayı yollar, sonra bağlantıyı keser; biz de oturumu kapatıp
    // giriş sayfasına döneriz. Eski token'la yeniden bağlanma denemesi sunucuda
    // 'session_revoked' ile reddedilir (olay kaçırıldıysa connect_error yakalar).
    const forceLogout = (message?: string) => {
      if (forcedOutRef.current) return;
      forcedOutRef.current = true;
      toast.warning(message || 'Oturumunuz sonlandırıldı, lütfen tekrar giriş yapın.', { autoClose: 6000 });
      socket.removeAllListeners();
      socket.disconnect();
      dispatch(_logOut());
      setTimeout(() => window.location.replace('/auth/login'), 800);
    };
    socket.on('session:revoked', (p: { message?: string }) => forceLogout(p?.message));
    socket.on('connect_error', (err: Error) => {
      if (err?.message === 'session_revoked') forceLogout();
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
      setSocketInstance(null);
      setConnected(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Toplam okunmamış -> Redux (topbar rozeti). Sohbet cache'i değiştikçe türetilir.
  useEffect(() => {
    const total = (conversationsData || []).reduce((s, c) => s + (c.unreadCount || 0), 0);
    dispatch(_setMessagesUnread(total));
  }, [conversationsData, dispatch]);

  // ── İlk mesaj yükleme + açma ────────────────────────────────────────────────
  const loadInitial = useCallback(
    async (conversationId: number) => {
      if (messagesRef.current[conversationId]) return; // zaten yüklü
      setLoadingByConv((p) => ({ ...p, [conversationId]: true }));
      try {
        const { results, hasMore } = await chatApi.getMessages(conversationId, { limit: 30 });
        setMessages((prev) => ({ ...prev, [conversationId]: results }));
        setHasMoreByConv((p) => ({ ...p, [conversationId]: hasMore }));
        const last = results[results.length - 1];
        if (last) markRead(conversationId, last.id);
      } catch (e) {
        // Erişim yok / geçici hata — sessizce geç (backend zaten 403 döner)
      } finally {
        setLoadingByConv((p) => ({ ...p, [conversationId]: false }));
      }
    },
    [markRead, setMessages],
  );

  const openConversation = useCallback(
    (conversationId: number) => {
      const prev = activeConvIdRef.current;
      const s = socketRef.current;
      if (prev && prev !== conversationId && s && s.connected) s.emit('conversation:close', { conversationId: prev });
      setActiveConvId(conversationId);
      if (s && s.connected) s.emit('conversation:open', { conversationId });
      loadInitial(conversationId);
    },
    [loadInitial],
  );

  const closeConversation = useCallback((conversationId: number) => {
    const s = socketRef.current;
    if (s && s.connected) s.emit('conversation:close', { conversationId });
    if (activeConvIdRef.current === conversationId) setActiveConvId(null);
  }, []);

  // Mesajlar sayfasından ayrılınca çağrılır: aktif sohbeti temizle (bildirimler her yerde çalışsın)
  const clearActive = useCallback(() => {
    const prev = activeConvIdRef.current;
    const s = socketRef.current;
    if (prev && s && s.connected) s.emit('conversation:close', { conversationId: prev });
    setActiveConvId(null);
  }, []);

  const loadOlder = useCallback(
    async (conversationId: number) => {
      const list = messagesRef.current[conversationId] || [];
      const before = list.reduce((min, m) => (m.id > 0 && m.id < min ? m.id : min), Number.POSITIVE_INFINITY);
      if (!Number.isFinite(before)) return;
      setLoadingByConv((p) => ({ ...p, [conversationId]: true }));
      try {
        const { results, hasMore } = await chatApi.getMessages(conversationId, { before, limit: 30 });
        setMessages((prev) => {
          const cur = prev[conversationId] || [];
          const existingIds = new Set(cur.map((m) => m.id));
          const older = results.filter((m) => !existingIds.has(m.id));
          return { ...prev, [conversationId]: [...older, ...cur] };
        });
        setHasMoreByConv((p) => ({ ...p, [conversationId]: hasMore }));
      } finally {
        setLoadingByConv((p) => ({ ...p, [conversationId]: false }));
      }
    },
    [setMessages],
  );

  // ── Gönderim (optimistic) ───────────────────────────────────────────────────
  const sendMessage = useCallback(
    (conversationId: number, payload: { content?: string; attachments?: ChatAttachment[]; replyToId?: number | null }) => {
      const tempId = `tmp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      const optimistic: ChatMessage = {
        id: -++tempCounterRef.current,
        tempId,
        pending: true,
        conversationId,
        senderId: myId,
        senderName: user?.name,
        senderSurname: user?.surname,
        content: payload.content || null,
        attachments: payload.attachments || [],
        replyToId: payload.replyToId ?? null,
        editedAt: null,
        deletedAt: null,
        reactions: [],
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => ({ ...prev, [conversationId]: [...(prev[conversationId] || []), optimistic] }));

      const body = { conversationId, tempId, content: payload.content, attachments: payload.attachments, replyToId: payload.replyToId };
      const s = socketRef.current;
      if (s && s.connected) {
        s.emit('message:send', body, (ack: any) => {
          if (ack && ack.ok === false) {
            setMessages((prev) => ({
              ...prev,
              [conversationId]: (prev[conversationId] || []).map((m) => (m.tempId === tempId ? { ...m, pending: false, failed: true } : m)),
            }));
          }
        });
      } else {
        chatApi
          .createMessage(conversationId, { content: payload.content, attachments: payload.attachments, replyToId: payload.replyToId })
          .then((real) => reconcileIncoming({ ...real, tempId }))
          .catch(() =>
            setMessages((prev) => ({
              ...prev,
              [conversationId]: (prev[conversationId] || []).map((m) => (m.tempId === tempId ? { ...m, pending: false, failed: true } : m)),
            })),
          );
      }
    },
    [myId, reconcileIncoming, setMessages, user],
  );

  const editMessage = useCallback((messageId: number, content: string) => {
    const s = socketRef.current;
    if (s && s.connected) s.emit('message:edit', { messageId, content });
    else chatApi.editMessage(messageId, content).catch(() => undefined);
  }, []);

  const deleteMessage = useCallback((messageId: number) => {
    const s = socketRef.current;
    if (s && s.connected) s.emit('message:delete', { messageId });
    else chatApi.deleteMessage(messageId).catch(() => undefined);
  }, []);

  const addReaction = useCallback((messageId: number, emoji: string) => {
    const s = socketRef.current;
    if (s && s.connected) s.emit('reaction:add', { messageId, emoji });
    else chatApi.addReaction(messageId, emoji).catch(() => undefined);
  }, []);

  const removeReaction = useCallback((messageId: number, emoji: string) => {
    const s = socketRef.current;
    if (s && s.connected) s.emit('reaction:remove', { messageId, emoji });
    else chatApi.removeReaction(messageId, emoji).catch(() => undefined);
  }, []);

  const startTyping = useCallback((conversationId: number) => {
    const s = socketRef.current;
    if (!s) return;
    const now = Date.now();
    const last = typingEmitRef.current[conversationId] || 0;
    if (now - last > 2500) {
      s.emit('typing:start', { conversationId });
      typingEmitRef.current[conversationId] = now;
    }
    clearTimeout(typingStopTimerRef.current[conversationId]);
    typingStopTimerRef.current[conversationId] = setTimeout(() => {
      const sock = socketRef.current;
      if (sock) sock.emit('typing:stop', { conversationId });
      typingEmitRef.current[conversationId] = 0;
    }, 3000);
  }, []);

  const stopTyping = useCallback((conversationId: number) => {
    const s = socketRef.current;
    if (s) s.emit('typing:stop', { conversationId });
    typingEmitRef.current[conversationId] = 0;
    clearTimeout(typingStopTimerRef.current[conversationId]);
  }, []);

  const joinChannel = useCallback(
    (conversationId: number) => {
      const s = socketRef.current;
      if (s) s.emit('channel:join', { conversationId }, () => queryClient.invalidateQueries(CONV_KEY));
    },
    [queryClient],
  );

  const leaveChannel = useCallback(
    (conversationId: number) => {
      const s = socketRef.current;
      if (s) s.emit('channel:leave', { conversationId }, () => queryClient.invalidateQueries(CONV_KEY));
    },
    [queryClient],
  );

  const isOnline = useCallback((userId: number) => onlineUsers.includes(userId), [onlineUsers]);

  const lastSeenOf = useCallback(
    (userId: number, fallback?: string | number | null): number | null => {
      const live = lastSeen[userId];
      if (live != null) return live;
      return toMs(fallback);
    },
    [lastSeen],
  );

  const trackRoute = useCallback((route: string, title?: string) => {
    const s = socketRef.current;
    if (s && s.connected) s.emit('activity:route', { route, title });
  }, []);

  const trackAction = useCallback((action: { type?: string; label: string }) => {
    const s = socketRef.current;
    if (s && s.connected && action && action.label) s.emit('activity:action', action);
  }, []);

  // Provider'ın state'i değişmeden yeniden render olması (örn. üst ağaç render'ı)
  // tüketicileri tetiklemesin diye referans sabitlenir.
  const value: ChatSocketContextValue = useMemo(
    () => ({
      connected,
      myId,
      onlineUsers,
      isOnline,
      lastSeenOf,
      typingByConv,
      readByConv,
      messagesByConv,
      hasMoreByConv,
      loadingByConv,
      activeConvId,
      notifications,
      dismissNotification,
      openConversation,
      closeConversation,
      clearActive,
      loadOlder,
      sendMessage,
      editMessage,
      deleteMessage,
      addReaction,
      removeReaction,
      startTyping,
      stopTyping,
      markRead,
      joinChannel,
      leaveChannel,
      socket: socketInstance,
      trackRoute,
      trackAction,
    }),
    [
      connected,
      myId,
      onlineUsers,
      isOnline,
      lastSeenOf,
      typingByConv,
      readByConv,
      messagesByConv,
      hasMoreByConv,
      loadingByConv,
      activeConvId,
      notifications,
      dismissNotification,
      openConversation,
      closeConversation,
      clearActive,
      loadOlder,
      sendMessage,
      editMessage,
      deleteMessage,
      addReaction,
      removeReaction,
      startTyping,
      stopTyping,
      markRead,
      joinChannel,
      leaveChannel,
      socketInstance,
      trackRoute,
      trackAction,
    ],
  );

  return <ChatSocketContext.Provider value={value}>{children}</ChatSocketContext.Provider>;
};
